import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Shield, ArrowLeft, ArrowRight, HeartHandshake, BookOpen,
  Sparkles, CheckCircle2, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import AppFooter from '../components/AppFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReflectionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [guidance, setGuidance] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('questions'); // questions | guidance

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: qs }, { data: latest }] = await Promise.all([
          axios.get(`${API_URL}/api/reflection/questions`),
          axios.get(`${API_URL}/api/reflection/latest`, { withCredentials: true }),
        ]);
        setQuestions(qs.questions || []);
        if (latest?.reflection?.guidance) {
          setGuidance(latest.reflection.guidance);
          const seeded = {};
          (latest.reflection.answers || []).forEach(a => { seeded[a.question_id] = a.answer; });
          setAnswers(seeded);
          setStep('guidance');
        }
      } catch (e) {
        toast.error('Could not load reflection');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const answeredCount = questions.filter(q => (answers[q.id] || '').trim().length > 0).length;
  const canSubmit = answeredCount >= 3 && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers)
          .filter(([, v]) => (v || '').trim().length > 0)
          .map(([question_id, answer]) => ({ question_id, answer })),
      };
      const { data } = await axios.post(
        `${API_URL}/api/reflection/submit`,
        payload,
        { withCredentials: true }
      );
      setGuidance(data.guidance);
      setStep('guidance');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit reflection');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="reflection-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center" data-testid="reflection-home-link">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </Link>
            <Link
              to="/dashboard"
              className="text-sm text-[#36454F]/70 hover:text-[#36454F] font-['Manrope'] flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Dashboard
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Hero */}
        <div className="mb-8 animate-fade-in-up">
          <p className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-medium mb-2">
            Before you dispute — reflect
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F]">
            Why are we here, {user?.name?.split(' ')[0] || 'friend'}?
          </h1>
          <p className="mt-3 text-[#36454F]/70 font-['Manrope'] leading-relaxed">
            Clearing credit errors is important. Clearing the <em>roots</em> of the debt is
            what keeps them from coming back. Take 5 honest minutes before we file any letters —
            we'll pair your answers with biblical wisdom you can actually walk out.
          </p>
        </div>

        {loading && (
          <Card className="border-[#36454F]/15 bg-white">
            <CardContent className="p-10 text-center text-[#36454F]/50 font-['Manrope']">
              <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin text-[#B76E79]" strokeWidth={1.5} />
              Loading…
            </CardContent>
          </Card>
        )}

        {!loading && step === 'questions' && (
          <Card className="border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-100" data-testid="reflection-questions-card">
            <CardContent className="p-6 sm:p-8 space-y-6">
              {questions.map((q, i) => (
                <div key={q.id} className="space-y-2">
                  <label
                    className="flex items-baseline gap-2 text-sm font-['Manrope'] font-medium text-[#36454F]"
                    htmlFor={`q-${q.id}`}
                  >
                    <span className="font-['Playfair_Display'] text-[#B76E79] text-lg leading-none">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span>{q.prompt}</span>
                  </label>
                  <Textarea
                    id={`q-${q.id}`}
                    value={answers[q.id] || ''}
                    onChange={e => setAnswers({ ...answers, [q.id]: e.target.value })}
                    className="border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] text-sm min-h-[70px]"
                    placeholder="Answer as honestly as you can. No one else sees this."
                    data-testid={`reflection-answer-${q.id}`}
                  />
                </div>
              ))}

              <div className="flex items-center justify-between pt-2 border-t border-[#36454F]/10">
                <p className="text-xs text-[#36454F]/50 font-['Manrope']">
                  {answeredCount} of {questions.length} answered {answeredCount < 3 && <span className="text-[#B76E79]">(need at least 3)</span>}
                </p>
                <Button
                  onClick={submit}
                  disabled={!canSubmit}
                  className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm disabled:opacity-50"
                  data-testid="reflection-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> Listening…</>
                  ) : (
                    <>Get your guidance <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} /></>
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-[#36454F]/45 font-['Manrope']">
                This is not financial advice. Selah Slate is a faith-based educational tool, not a lender, advisor, or therapist. Always consult qualified professionals for major decisions.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && step === 'guidance' && guidance && (
          <div className="space-y-5 animate-fade-in-up animation-delay-100" data-testid="reflection-guidance">
            {/* Observations */}
            {Array.isArray(guidance.observations) && guidance.observations.length > 0 && (
              <Card className="border-[#36454F]/15 bg-white">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center gap-2">
                    <HeartHandshake className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                    <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                      What we're hearing
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {guidance.observations.map((o, i) => (
                      <li key={i} className="text-sm text-[#36454F]/80 font-['Manrope'] leading-relaxed flex items-start gap-2">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#B76E79] flex-shrink-0" />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Scripture */}
            {guidance.scripture && (
              <Card className="border-[#E0BFB8] bg-[#E0BFB8]/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                    <p className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-semibold">
                      A word from scripture
                    </p>
                  </div>
                  <p className="font-['Playfair_Display'] text-lg italic text-[#36454F] leading-relaxed" data-testid="reflection-scripture-verse">
                    "{guidance.scripture.verse}"
                  </p>
                  <p className="text-sm text-[#36454F]/55 font-['Manrope'] mt-2">
                    — {guidance.scripture.reference}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Principles */}
            {Array.isArray(guidance.principles) && guidance.principles.length > 0 && (
              <Card className="border-[#36454F]/15 bg-white">
                <CardContent className="p-6 space-y-3">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                    Principles to anchor you
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {guidance.principles.map((p, i) => (
                      <div key={i} className="p-3 rounded-sm bg-[#F5F5F5] border border-[#36454F]/10 text-sm text-[#36454F]/85 font-['Manrope']">
                        {p}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Next steps */}
            {Array.isArray(guidance.next_steps) && guidance.next_steps.length > 0 && (
              <Card className="border-[#4CAF50]/30 bg-[#4CAF50]/5">
                <CardContent className="p-6 space-y-3">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#4CAF50]" strokeWidth={1.5} />
                    Three steps this week
                  </h3>
                  <ul className="space-y-2">
                    {guidance.next_steps.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#36454F]/85 font-['Manrope']">
                        <CheckCircle2 className="w-4 h-4 text-[#4CAF50] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Encouragement */}
            {guidance.encouragement && (
              <Card className="border-[#36454F]/15 bg-gradient-to-br from-[#36454F] to-[#4a5a66] text-white">
                <CardContent className="p-6">
                  <p className="font-['Playfair_Display'] italic text-base leading-relaxed">
                    {guidance.encouragement}
                  </p>
                </CardContent>
              </Card>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button
                onClick={() => { setStep('questions'); window.scrollTo({top:0,behavior:'smooth'}); }}
                variant="outline"
                className="border-[#36454F]/30 text-[#36454F] font-['Manrope'] rounded-sm"
                data-testid="reflection-retry-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Revisit my answers
              </Button>
              <Button
                onClick={() => navigate('/upload')}
                className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                data-testid="reflection-continue-btn"
              >
                Ready — let's dispute
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}
      </main>

      <AppFooter />
    </div>
  );
}
